import { useEffect, useRef, useState, type RefObject } from "react";
import { AxiosError } from "axios";

import type { ApplicationDetail } from "@/api/applications";
import type { CadDetails, CadSimilarCandidate } from "@/api/cad";
import { PennButton, PennText } from "@/components/ui";
import { useApplication } from "@/hooks/useApplications";
import {
  useCadDetails,
  useCadGlbUrl,
  useCadSearch,
  useCadStatus,
  useCadVolume,
} from "@/hooks/useCad3d";
import { useAppSelector } from "@/stores/hooks";
import { estimateWeights } from "@/utils/glb-volume";
import type { ModelViewerElement } from "@/types/model-viewer";
import { applyMatteGrey } from "./applyMatteGrey";
import { Cad3dStatusChip } from "./Cad3dStatusChip";
import { ensureModelViewer } from "./loadModelViewer";

// 2-pane "3D Part Analysis" — parity with the POC stepcheck.html: Incoming part
// (left) vs the most-similar library part (right), with linked camera views, a
// Fit reset, a similar-part picker, and a properties panel under each viewer.
// Similarity matches are internal-only (spec §6) — the page that renders this
// component gates on isInternal, and the search call returns 403 for external
// callers. No upload here: uploading lives on the single-preview screen.

const MM_PER_IN = 25.4;
const DEFAULT_ORBIT = "0deg 75deg auto";
const DEFAULT_TARGET = "auto auto auto";

type Dims = { x: number; y: number; z: number } | null;
type ViewerState = "idle" | "loading" | "loaded" | "error";

type Cad3dCompareProps = {
  applicationId: string;
  viewerHeight?: number;
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? fallback;
  }
  return fallback;
}

function fmt(n: number, digits = 1): string {
  return Number.isFinite(n) ? n.toFixed(digits) : "—";
}

type PropRow = { label: string; value: string };
type PropGroup = { title: string; rows: PropRow[] };

function fmtDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function fmtNumber(value: number | null | undefined): string | null {
  return value == null || !Number.isFinite(value) ? null : value.toLocaleString();
}

// Geometry facts (bbox dims from the loaded GLB + signature metadata from svc-09).
function geometryRows(
  details: CadDetails | undefined,
  dims: Dims,
  candidate?: CadSimilarCandidate | null,
): PropRow[] {
  const rows: PropRow[] = [];
  rows.push({ label: "File", value: details?.fileName ?? "—" });

  if (dims) {
    const sorted = [dims.x, dims.y, dims.z].sort((a, b) => b - a);
    const [L, W, H] = sorted;
    rows.push({
      label: "Size (L × W × H)",
      value:
        `${fmt(L, 2)} × ${fmt(W, 2)} × ${fmt(H, 2)} mm  ·  ` +
        `${fmt(L / MM_PER_IN, 2)} × ${fmt(W / MM_PER_IN, 2)} × ${fmt(H / MM_PER_IN, 2)} in`,
    });
  } else {
    rows.push({ label: "Size (L × W × H)", value: "—" });
  }

  if (details?.signatureDiagonalMm != null) {
    rows.push({
      label: "Bounding-box diagonal",
      value: `${fmt(details.signatureDiagonalMm, 2)} mm`,
    });
  }
  if (details?.meshes != null || details?.vertices != null) {
    rows.push({
      label: "Mesh",
      value: `${details?.meshes ?? "—"} meshes · ${details?.vertices ?? "—"} vertices`,
    });
  }
  const t = details?.tessellation;
  if (t && (t.linearDeflection != null || t.angularDeflection != null)) {
    rows.push({
      label: "Tessellation",
      value:
        `linear ${t.linearDeflection ?? "—"} ${t.linearUnit ?? "mm"} · ` +
        `angular ${t.angularDeflection ?? "—"}`,
    });
  }
  if (details?.engineVersion) {
    rows.push({ label: "Signature engine", value: details.engineVersion });
  }
  if (candidate) {
    rows.push({
      label: "Similarity",
      value: `${fmt(candidate.score * 100, 1)}%${candidate.isMatch ? " · match" : ""}`,
    });
  }
  return rows;
}

// Estimated weight from the client-computed mesh volume (POC stepcheck parity).
function weightRows(volumeMm3: number | null | undefined): PropRow[] {
  const w = estimateWeights(volumeMm3 ?? null);
  if (!w) return [];
  return [
    { label: "Steel", value: `${fmt(w.steelKg, 3)} kg (${fmt(w.steelLb, 2)} lb)` },
    { label: "Aluminum", value: `${fmt(w.alKg, 3)} kg (${fmt(w.alLb, 2)} lb)` },
  ];
}

// Business context from the application record (POC HUD "Application" section).
// `fallbackId` keeps the POC behaviour of showing just the id when the record
// can't be read (e.g. a similar part owned by another user — read returns 404).
function applicationRows(
  app: ApplicationDetail | undefined,
  companyName: string | null,
  fallbackId: string | null,
): PropRow[] {
  const rows: PropRow[] = [];
  if (companyName) rows.push({ label: "Company", value: companyName });
  if (!app) {
    if (fallbackId) rows.push({ label: "Application ID", value: fallbackId });
    return rows;
  }
  const push = (label: string, value: string | null | undefined) => {
    if (value != null && String(value).trim() !== "") {
      rows.push({ label, value: String(value) });
    }
  };
  push("Application name", app.name);
  push("Application number", app.applicationNumber);
  push("OEM", app.oem);
  push("Production program", app.productionProgram);
  push("Annual production volume", fmtNumber(app.annualVolume));
  push("Average batch size", fmtNumber(app.batchSize));
  push("Material", app.materialCode);
  push("Created by", app.createdByEmail);
  push("Created at", fmtDate(app.createdAt));
  push("Last updated", fmtDate(app.updatedAt));
  return rows;
}

// Fastener line items (POC HUD "Fasteners" section: Brand · Type · Qty).
function fastenerRows(app: ApplicationDetail | undefined): PropRow[] {
  if (!app || app.fasteners.length === 0) return [];
  return app.fasteners.map((f, i) => ({
    label: `#${i + 1}`,
    value:
      `Brand: ${f.brand ?? "—"} · Type: ${f.type ?? "—"} · ` +
      `Qty: ${f.qtyPerPart ?? f.qtyPerPartA ?? f.qtyPerPartB ?? "—"}`,
  }));
}

function buildGroups(args: {
  details: CadDetails | undefined;
  dims: Dims;
  volumeMm3: number | null | undefined;
  app: ApplicationDetail | undefined;
  companyName: string | null;
  fallbackId: string | null;
  candidate?: CadSimilarCandidate | null;
}): PropGroup[] {
  const groups: PropGroup[] = [
    { title: "Geometry", rows: geometryRows(args.details, args.dims, args.candidate) },
  ];
  const weights = weightRows(args.volumeMm3);
  if (weights.length) groups.push({ title: "Estimated weight", rows: weights });
  const appRows = applicationRows(args.app, args.companyName, args.fallbackId);
  if (appRows.length) groups.push({ title: "Application", rows: appRows });
  const fasteners = fastenerRows(args.app);
  if (fasteners.length) groups.push({ title: "Fasteners", rows: fasteners });
  return groups;
}

const Overlay = ({ children }: { children: React.ReactNode }) => (
  <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-center text-sm text-gray-400">
    {children}
  </div>
);

// Self-contained robust viewer: remounts cleanly per `src` (key), reports the
// loaded bounding box up via onDims, and surfaces load failures explicitly
// (model-viewer fails silently otherwise — the cause of the earlier blank pane).
const ViewerBox = ({
  src,
  elementRef,
  height,
  idleText,
  onDims,
}: {
  src: string | undefined;
  elementRef: RefObject<ModelViewerElement | null>;
  height: number;
  idleText: string;
  onDims?: (d: Dims) => void;
}) => {
  const innerRef = useRef<ModelViewerElement | null>(null);
  const [state, setState] = useState<ViewerState>(src ? "loading" : "idle");
  const [progress, setProgress] = useState(0);

  const setRef = (el: ModelViewerElement | null) => {
    innerRef.current = el;
    elementRef.current = el;
  };

  useEffect(() => {
    if (!src) {
      setState("idle");
      onDims?.(null);
      return;
    }
    setState("loading");
    setProgress(0);
    const el = innerRef.current;
    if (!el) return;
    const onLoad = () => {
      setState("loaded");
      applyMatteGrey(el); // POC matte-grey tone (NEW Task#2)
      try {
        onDims?.(el.getDimensions());
      } catch {
        onDims?.(null);
      }
    };
    const onErr = () => setState("error");
    // C7 (POC parity stepcheck.html load progress bar :271-273): model-viewer
    // reports GLB fetch/decode progress via the `progress` event
    // (detail.totalProgress 0→1) — drive the inline load bar with it.
    const onProgress = (e: Event) => {
      const detail = (e as CustomEvent<{ totalProgress?: number }>).detail;
      if (detail && typeof detail.totalProgress === "number") {
        setProgress(detail.totalProgress);
      }
    };
    el.addEventListener("load", onLoad);
    el.addEventListener("error", onErr);
    el.addEventListener("progress", onProgress);
    return () => {
      el.removeEventListener("load", onLoad);
      el.removeEventListener("error", onErr);
      el.removeEventListener("progress", onProgress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (!src) {
    return (
      <div
        className="flex items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-center text-sm text-gray-400"
        style={{ height }}
      >
        {idleText}
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-gray-200 bg-[#fafafa]"
      style={{height:"75vh"}}
    >
      {/* POC parity (stepcheck.html L1151-1159): grey #fafafa backdrop, dim
          lighting (exposure ↓) + neutral tone-mapping so the grey #ccc part
          reads matte/greyish rather than the bright PBR "whiteish" default. */}
      <model-viewer
        key={src}
        ref={setRef}
        src={src}
        alt="CAD part preview"
        camera-controls
        interaction-prompt="none"
        shadow-intensity="1"
        exposure="0.78"
        tone-mapping="neutral"
        camera-orbit={DEFAULT_ORBIT}
        style={{ width: "100%", height: "100%", backgroundColor: "#fafafa" }}
      />
      {state === "loading" && (
        <Overlay>Loading preview… {Math.round(progress * 100)}%</Overlay>
      )}
      {state === "loading" && (
        <div className="absolute inset-x-0 bottom-0 h-1.5 bg-gray-200">
          <div
            className="h-full bg-penn-500 transition-[width] duration-150 ease-out"
            style={{ width: `${Math.max(4, Math.round(progress * 100))}%` }}
          />
        </div>
      )}
      {state === "error" && (
        <Overlay>Preview failed to load (the GLB may be missing or expired).</Overlay>
      )}
    </div>
  );
};

const PropertiesPanel = ({
  groups,
  footnote,
}: {
  groups: PropGroup[];
  footnote?: string;
}) => (
  <div className="mt-3 space-y-3 rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3">
    {groups.map((group) => (
      <div key={group.title}>
        <p className="mb-1 text-[11px] font-bold uppercase text-gray-400">
          {group.title}
        </p>
        <dl className="grid grid-cols-[minmax(120px,auto)_1fr] gap-x-4 gap-y-1.5">
          {group.rows.map((r) => (
            <div key={`${group.title}-${r.label}`} className="contents">
              <dt className="text-xs font-semibold text-gray-500">{r.label}</dt>
              <dd className="text-xs text-gray-800 break-words">{r.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    ))}
    {footnote && (
      <p className="mt-2 text-[11px] leading-snug text-gray-400">{footnote}</p>
    )}
  </div>
);

export const Cad3dCompare = ({
  applicationId,
  viewerHeight = 420,
}: Cad3dCompareProps) => {
  const statusQuery = useCadStatus(applicationId);
  const status = statusQuery.data?.status ?? null;
  const hasPreview = statusQuery.data?.hasPreview ?? false;

  const incomingGlb = useCadGlbUrl(applicationId, hasPreview);
  const incomingDetails = useCadDetails(applicationId, hasPreview);
  const search = useCadSearch(applicationId, hasPreview);
  // The "Similar part" panel must list ONLY library parts that MEET the match
  // threshold (score ≥ cad3d_settings.match_threshold). svc-09 also returns the
  // lower-scoring candidates (kept "for internal tuning" — services/search.py),
  // so filter to the matches here; Cad3dCompare is the sole consumer of this
  // search. `isMatch` already encodes score ≥ threshold (inclusive), computed
  // server-side, so no client-side re-derivation of the cutoff.
  const rawCandidates = search.data?.candidates ?? [];
  const candidates = rawCandidates.filter((c) => c.isMatch);
  const matchThresholdPct =
    search.data != null ? Math.round(search.data.matchThreshold * 100) : null;

  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  useEffect(() => {
    if (selectedMatchId == null && candidates.length > 0) {
      setSelectedMatchId(candidates[0].applicationId);
    }
  }, [candidates, selectedMatchId]);
  const selectedCandidate =
    candidates.find((c) => c.applicationId === selectedMatchId) ?? null;

  const matchGlb = useCadGlbUrl(selectedMatchId, !!selectedMatchId);
  const matchDetails = useCadDetails(selectedMatchId, !!selectedMatchId);

  // Business context + estimated weight (POC HUD parity). The incoming part is the
  // caller's own application; the similar part may be owned by another user, so its
  // record read is best-effort (404 → fall back to the application id).
  const companyName = useAppSelector((s) => s.auth.user?.companyName ?? null);
  const incomingApp = useApplication(applicationId);
  const matchApp = useApplication(selectedMatchId);
  const incomingVolume = useCadVolume(incomingGlb.data?.url);
  const matchVolume = useCadVolume(matchGlb.data?.url);

  const [linked, setLinked] = useState(true);
  const linkedRef = useRef(linked);
  useEffect(() => {
    linkedRef.current = linked;
  }, [linked]);

  const [viewerReady, setViewerReady] = useState(false);
  const [viewerFailed, setViewerFailed] = useState(false);
  useEffect(() => {
    let active = true;
    ensureModelViewer()
      .then(() => active && setViewerReady(true))
      .catch(() => active && setViewerFailed(true));
    return () => {
      active = false;
    };
  }, []);

  const leftRef = useRef<ModelViewerElement | null>(null);
  const rightRef = useRef<ModelViewerElement | null>(null);
  const [leftDims, setLeftDims] = useState<Dims>(null);
  const [rightDims, setRightDims] = useState<Dims>(null);

  const leftSrc = incomingGlb.data?.url;
  const rightSrc = matchGlb.data?.url;

  // Linked camera: mirror one viewer's orbit onto the other on user interaction.
  useEffect(() => {
    if (!viewerReady) return;
    const left = leftRef.current;
    const right = rightRef.current;
    if (!left || !right) return;
    let syncing = false;
    const mirror =
      (from: ModelViewerElement, to: ModelViewerElement) => (e: Event) => {
        if (!linkedRef.current) return;
        const detail = (e as CustomEvent<{ source?: string }>).detail;
        if (detail?.source !== "user-interaction" || syncing) return;
        syncing = true;
        try {
          const o = from.getCameraOrbit();
          to.cameraOrbit = `${o.theta}rad ${o.phi}rad ${o.radius}m`;
        } finally {
          requestAnimationFrame(() => {
            syncing = false;
          });
        }
      };
    const onLeft = mirror(left, right);
    const onRight = mirror(right, left);
    left.addEventListener("camera-change", onLeft);
    right.addEventListener("camera-change", onRight);
    return () => {
      left.removeEventListener("camera-change", onLeft);
      right.removeEventListener("camera-change", onRight);
    };
  }, [viewerReady, leftSrc, rightSrc]);

  const onFit = () => {
    for (const el of [leftRef.current, rightRef.current]) {
      if (!el) continue;
      el.cameraOrbit = DEFAULT_ORBIT;
      el.cameraTarget = DEFAULT_TARGET;
      el.fieldOfView = "auto";
    }
  };

  const dimsFootnote =
    "Size is the axis-aligned bounding box (mm and inch). Estimated weight is derived" +
    " from the mesh volume (steel/aluminium) and shown only when the part is watertight.";

  if (!hasPreview) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center">
        <Cad3dStatusChip status={status} />
        <PennText as="p" size="sm" color="blue_grey" className="mt-3">
          {status === "pending" || status === "running"
            ? "Analysis is still running — the comparison appears once the preview is ready."
            : "No 3D preview yet. Upload a STEP file for this application on the part-check screen first."}
        </PennText>
      </div>
    );
  }

  if (viewerFailed) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        3D viewer could not be loaded.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div >
        <div className="flex items-center gap-3">
        <PennText as="h2" size="lg" font="semibold" color="blue_grey">
            3D Part Analysis
          </PennText>
          <Cad3dStatusChip status={status} />
        </div>
        <PennText as="p" size="sm"  color="blue_grey">
           3D part comparison uploaded part against the most similar parts in the library.
          </PennText>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <label
            className="flex cursor-pointer items-center gap-2"
            title="Rotate/zoom one model and the other follows"
          >
            <input
              type="checkbox"
              checked={linked}
              onChange={(e) => setLinked(e.target.checked)}
            />
            Link views
          </label>
          <PennButton type="button" variant="secondary" size="sm" onClick={onFit}>
            Fit
          </PennButton>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Incoming part */}
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <PennText as="span" size="sm" font="semibold" color="blue_grey">
              Incoming part
            </PennText>
            <PennText as="span" size="xs" color="blue_grey" className="truncate">
              {incomingDetails.data?.fileName ?? statusQuery.data?.fileName ?? "—"}
            </PennText>
          </div>
          <ViewerBox
            src={leftSrc}
            elementRef={leftRef}
            height={viewerHeight}
            idleText={viewerReady ? "Loading preview…" : "Loading 3D viewer…"}
            onDims={setLeftDims}
          />
          <PropertiesPanel
            groups={buildGroups({
              details: incomingDetails.data,
              dims: leftDims,
              // Prefer svc-09's exact BREP volume; fall back to the client GLB
              // mesh volume only when the server value isn't populated yet.
              volumeMm3: incomingDetails.data?.volumeMm3 ?? incomingVolume.data,
              app: incomingApp.data,
              companyName,
              fallbackId: applicationId,
            })}
            footnote={dimsFootnote}
          />
        </div>

        {/* Similar part */}
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <PennText as="span" size="sm" font="semibold" color="blue_grey">
              Similar part
            </PennText>
            {candidates.length > 0 && (
              <select
                aria-label="Select similar part"
                className="max-w-[60%] truncate rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700"
                value={selectedMatchId ?? ""}
                onChange={(e) => setSelectedMatchId(e.target.value)}
              >
                {candidates.map((c) => (
                  <option key={c.applicationId} value={c.applicationId}>
                    {(c.fileName ?? c.applicationId.slice(0, 8))} —{" "}
                    {fmt(c.score * 100, 1)}%
                  </option>
                ))}
              </select>
            )}
          </div>

          {search.isError ? (
            <div
              className="flex items-center justify-center rounded-xl border border-amber-200 bg-amber-50 text-center text-sm text-amber-700"
              style={{ height: viewerHeight }}
            >
              {errorMessage(search.error, "Similarity search is unavailable.")}
            </div>
          ) : candidates.length === 0 ? (
            <div
              className="flex items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-center text-sm text-gray-400"
              style={{ height: viewerHeight }}
            >
              {search.isLoading
                ? "Searching the library…"
                : rawCandidates.length > 0 && matchThresholdPct != null
                  ? `No parts met the ${matchThresholdPct}% similarity threshold.`
                  : "No comparable parts in the library yet."}
            </div>
          ) : (
            <ViewerBox
              src={rightSrc}
              elementRef={rightRef}
              height={viewerHeight}
              idleText={
                matchGlb.isError
                  ? "No preview available for this match."
                  : "Loading preview…"
              }
              onDims={setRightDims}
            />
          )}

          {candidates.length > 0 && (
            <PropertiesPanel
              groups={buildGroups({
                details: matchDetails.data,
                dims: rightDims,
                volumeMm3: matchDetails.data?.volumeMm3 ?? matchVolume.data,
                app: matchApp.data,
                companyName: null,
                fallbackId: selectedMatchId,
                candidate: selectedCandidate,
              })}
              footnote={dimsFootnote}
            />
          )}
        </div>
      </div>
    </div>
  );
};
